import React, { useState, useEffect } from "react";
import { Box, TextField, IconButton, Tabs, Tab, FormControlLabel, Switch } from "@material-ui/core";
import { Send } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import ReferenceImageThumbnail from "./ReferenceImageThumbnail";
import DrawingCanvas from "./DrawingCanvas";
import CameraCapture from "./CameraCapture";
import LanguageSelector from "./LanguageSelector";
import ImageUploadTab from "./ImageUploadTab";
import { useTabIndices } from "./useTabIndices";
import { urlToDataUrl } from "./imageUtils";

type ChatInputProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: (prompt: string, referenceImage: string | null) => void;
  disabled: boolean;
  chatImageUrl?: string;
  chatImageDataUrl?: string;
  currentExerciseImageUrl?: string;
  isFirstMessage?: boolean;
  localeIndex?: number;
  translations?: Array<{ locale: string }>;
  onPreviousLocale?: () => void;
  onNextLocale?: () => void;
};

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  disabled,
  chatImageUrl,
  chatImageDataUrl,
  currentExerciseImageUrl,
  isFirstMessage = false,
  localeIndex = 0,
  translations = [],
  onPreviousLocale,
  onNextLocale,
}) => {
  const { t } = useTranslation();
  const tabIndices = useTabIndices(!!chatImageUrl, !!currentExerciseImageUrl);

  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [drawnImage, setDrawnImage] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [currentExerciseImageDataUrl, setCurrentExerciseImageDataUrl] = useState<string | null>(null);

  // Determine default state for useReferenceImage
  // - If there's a chat history (not first message) AND a generated image exists: default ON
  // - If there's a current exercise image: default ON
  // - Otherwise: default OFF
  const shouldDefaultToOn = (!isFirstMessage && !!chatImageUrl) || !!currentExerciseImageUrl;
  const [useReferenceImage, setUseReferenceImage] = useState<boolean>(shouldDefaultToOn);
  const [tabValue, setTabValue] = useState<number>(0);

  // Auto-switch to appropriate tab when images become available
  useEffect(() => {
    if (chatImageUrl && !isFirstMessage) {
      setUseReferenceImage(true);
      setTabValue(tabIndices.chatImage);
    } else if (currentExerciseImageUrl && isFirstMessage) {
      setUseReferenceImage(true);
      setTabValue(tabIndices.currentExerciseImage);
    }
  }, [chatImageUrl, currentExerciseImageUrl, isFirstMessage, tabIndices]);

  // Convert current exercise image URL to base64 data URL
  useEffect(() => {
    const convertImage = async () => {
      if (!currentExerciseImageUrl) {
        setCurrentExerciseImageDataUrl(null);
        return;
      }

      try {
        const dataUrl = await urlToDataUrl(currentExerciseImageUrl);
        setCurrentExerciseImageDataUrl(dataUrl);
      } catch (error) {
        console.error("Error converting current exercise image URL to data URL:", error);
        setCurrentExerciseImageDataUrl(null);
      }
    };

    convertImage();
  }, [currentExerciseImageUrl]);

  // Get the current reference image based on active tab
  const getCurrentReferenceImage = (): string | null => {
    if (!useReferenceImage) return null;

    if (tabValue === tabIndices.chatImage && chatImageUrl) {
      return chatImageDataUrl || null;
    } else if (tabValue === tabIndices.currentExerciseImage && currentExerciseImageUrl) {
      return currentExerciseImageDataUrl;
    } else if (tabValue === tabIndices.upload) {
      return uploadedImage;
    } else if (tabValue === tabIndices.draw) {
      return drawnImage;
    } else if (tabValue === tabIndices.camera) {
      return capturedImage;
    }

    return null;
  };

  const handleDrawingComplete = (dataUrl: string) => {
    setDrawnImage(dataUrl || null);
  };

  const handleCaptureComplete = (dataUrl: string) => {
    setCapturedImage(dataUrl || null);
  };

  const handleSend = () => {
    if (value.trim()) {
      // Send the current reference image based on active tab
      const currentReference = getCurrentReferenceImage();
      onSend(value, currentReference);

      // Don't clear reference images - let the user keep them for retry if needed
      // They can manually remove them if they want to change the reference
      // Only the input text will be cleared by the parent component on success
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Send on Enter (without Shift)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTabChange = (_e: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
    // Each tab now has its own state, so no need to transfer images
  };

  return (
    <Box>
      {/* Language selector - only shown for first message */}
      {isFirstMessage && (
        <LanguageSelector
          localeIndex={localeIndex}
          translations={translations}
          onPreviousLocale={onPreviousLocale}
          onNextLocale={onNextLocale}
        />
      )}

      <Box display="flex" alignItems="flex-end" mb={1}>
        <TextField
          fullWidth
          multiline
          maxRows={6}
          variant="outlined"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t("imageGenerator.promptPlaceholder")}
          disabled={disabled}
        />
        <Box ml={1}>
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={disabled || !value.trim()}
            aria-label={t("imageGenerator.sendMessage")}
          >
            <Send />
          </IconButton>
        </Box>
      </Box>

      {/* Reference image toggle */}
      <Box mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={useReferenceImage}
              onChange={e => setUseReferenceImage(e.target.checked)}
              color="primary"
              disabled={disabled}
            />
          }
          label={t("imageGenerator.useReferenceImage")}
        />

        {useReferenceImage && (
          <>
            <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
              {chatImageUrl && <Tab label={t("imageGenerator.chatImage")} />}
              {currentExerciseImageUrl && <Tab label={t("imageGenerator.currentExerciseImage")} />}
              <Tab label={t("imageGenerator.uploadReference")} />
              <Tab label={t("imageGenerator.drawReference")} />
              <Tab label={t("imageGenerator.cameraReference")} />
            </Tabs>

            <Box mt={2}>
              {/* Chat image tab (only shown when image exists) */}
              {chatImageUrl && tabValue === tabIndices.chatImage && (
                <Box display="flex" justifyContent="center">
                  <ReferenceImageThumbnail imageUrl={chatImageUrl} />
                </Box>
              )}

              {/* Current Exercise Image tab (only shown when image exists) */}
              {currentExerciseImageUrl && tabValue === tabIndices.currentExerciseImage && (
                <Box display="flex" justifyContent="center">
                  <ReferenceImageThumbnail imageUrl={currentExerciseImageUrl} />
                </Box>
              )}

              {/* Upload tab */}
              {tabValue === tabIndices.upload && (
                <ImageUploadTab uploadedImage={uploadedImage} onImageUploaded={setUploadedImage} />
              )}

              {/* Draw tab */}
              {tabValue === tabIndices.draw && (
                <>
                  {drawnImage && (
                    <Box display="flex" justifyContent="center" mb={2}>
                      <ReferenceImageThumbnail imageUrl={drawnImage} />
                    </Box>
                  )}
                  <DrawingCanvas onDrawingComplete={handleDrawingComplete} />
                </>
              )}

              {/* Camera tab */}
              {tabValue === tabIndices.camera && (
                <>
                  {capturedImage && (
                    <Box display="flex" justifyContent="center" mb={2}>
                      <ReferenceImageThumbnail imageUrl={capturedImage} />
                    </Box>
                  )}
                  <CameraCapture onCaptureComplete={handleCaptureComplete} />
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ChatInput;
